/*
 * The console's on-screen keyboard (`pages/server/console/keyboard.tsx`)
 * speaks X11 keysyms; a terminal wants the bytes a terminal emulator sends.
 * This maps one to the other (xterm's defaults, normal cursor mode).
 */

const CTRL = 0xffe3;
const ALT = 0xffe9;
const SHIFT = 0xffe1;

const ESC = "\x1b";

/** Keys with their own escape sequence, by keysym. */
const SEQUENCES: Record<number, string> = {
  0xff1b: ESC, // Escape
  0xff09: "\t", // Tab
  0xff0d: "\r", // Return
  0xff08: "\x7f", // BackSpace (DEL, as xterm sends it)
  0xffff: `${ESC}[3~`, // Delete
  0xff50: `${ESC}[H`, // Home
  0xff57: `${ESC}[F`, // End
  0xff55: `${ESC}[5~`, // Page Up
  0xff56: `${ESC}[6~`, // Page Down
  0xff51: `${ESC}[D`, // Left
  0xff52: `${ESC}[A`, // Up
  0xff53: `${ESC}[C`, // Right
  0xff54: `${ESC}[B`, // Down
  0xffbe: `${ESC}OP`, // F1
  0xffbf: `${ESC}OQ`,
  0xffc0: `${ESC}OR`,
  0xffc1: `${ESC}OS`,
  0xffc2: `${ESC}[15~`, // F5
  0xffc3: `${ESC}[17~`,
  0xffc4: `${ESC}[18~`,
  0xffc5: `${ESC}[19~`,
  0xffc6: `${ESC}[20~`,
  0xffc7: `${ESC}[21~`,
  0xffc8: `${ESC}[23~`,
  0xffc9: `${ESC}[24~`, // F12
};

/** Ctrl with a character: the control code (`Ctrl+C` → 0x03), or null when it has none. */
export function control(char: string): string | null {
  const code = char.toUpperCase().charCodeAt(0);
  if (code >= 0x40 && code <= 0x5f) return String.fromCharCode(code - 0x40);
  if (char === " ") return "\0";
  if (char === "?") return "\x7f";
  return null;
}

/**
 * The bytes a key press sends to the shell: `keysym` is the key (a
 * printable keysym is its character), `held` the modifiers held with it.
 */
export function keysymToTerminal(keysym: number, held: readonly number[] = []): string {
  const ctrl = held.includes(CTRL);
  const alt = held.includes(ALT);
  const shift = held.includes(SHIFT);
  let out = SEQUENCES[keysym];
  if (out === undefined) {
    if (keysym < 0x20 || keysym > 0x7e) return "";
    let char = String.fromCharCode(keysym);
    if (shift) char = char.toUpperCase();
    out = ctrl ? (control(char) ?? char) : char;
  } else if (shift && keysym === 0xff09) {
    out = `${ESC}[Z`; // Shift+Tab
  }
  return alt ? `${ESC}${out}` : out;
}

/**
 * Ctrl+Shift+F (⌘+Shift+F on a Mac) inside a terminal opens «Найти в
 * выводе». By the key's place, not its letter: the same key on a Russian
 * layout gives «А».
 */
export const isSearchShortcut = (
  event: Pick<KeyboardEvent, "ctrlKey" | "metaKey" | "shiftKey" | "altKey" | "code">,
): boolean =>
  (event.ctrlKey || event.metaKey) && event.shiftKey && !event.altKey && event.code === "KeyF";
